import * as React from 'react';
import type {Asset, AssetGroup} from '@deviceinsight/ng-ui-api-client';
import type {DeviceActionState, DeviceActionType, UiAction, UiDeviceAction} from '../../../modules/ruleengine/types';
import RuleAsset from '../assetselection/RuleAsset';
import {
	getActionElementsForDeviceActionType,
	getGpiosWithOutputMode,
	initDeviceActionState,
	validateDeviceActionState
} from './ruleActionHelpers';
import {ACTION_TYPE_DEVICE, GPIO_ACTION_VALUE} from '../../../modules/ruleengine/config/consts';
import DeviceAndActionIcon from '../../common/DeviceAndActionIcon';
import {getAssetDeviceType} from '../ruleEditHelpers';
import RuleActionDeviceValue from './RuleActionDeviceValue';
import RuleActionDatapoint from './RuleActionDatapoint';
import RuleDeviceActionType from './RuleDeviceActionType';
import {
	DATAPOINT_BUTTON_COLOR1,
	DATAPOINT_BUTTON_COLOR2,
	DATAPOINT_LED_COLOR,
	DATAPOINT_PIN1,
	DATAPOINT_PIN2,
	DATAPOINT_PIN3,
	DATAPOINT_PIN4
} from '../../../modules/common/consts';
type Props = {
	action: UiDeviceAction;
	index: number;
	onChange: (arg0: UiAction, arg1: number) => void;
	assetGroup: AssetGroup;
	canEdit: boolean;
};
export default class RuleActionDevice extends React.PureComponent<Props, DeviceActionState> {
	state: DeviceActionState = {
		deviceActionType: undefined,
		targetValue: undefined,
		asset: undefined,
		dataPoint: undefined,
		targetFrequency: undefined,
		availableElements: [],
		selectedElement: undefined,
		availablePins: [],
		availableDeviceActionTypes: []
	};

	componentDidMount(): any {
		const {action} = this.props;
		initDeviceActionState(action).then((state) => this.setState(state));
	}

	onAssetSelect = (asset: Asset) => {
		// RAFI-300 get the GPIO Datapoints and allow only those who are in mode OUTPUT
		getGpiosWithOutputMode(asset).then((availablePins) => {
			this.validateAndCommit({
				asset,
				deviceActionType: undefined,
				targetValue: undefined,
				dataPoint: undefined,
				targetFrequency: undefined,
				availableElements: undefined,
				selectedElement: undefined,
				availablePins
			});
		});
	};
	onActionTypeSelect = (deviceActionType: DeviceActionType | null | undefined) => {
		const {asset, targetValue, targetFrequency, availablePins} = this.state;

		if (asset) {
			const deviceType = getAssetDeviceType(asset);
			const availableElements = getActionElementsForDeviceActionType(deviceType, deviceActionType, availablePins);
			this.validateAndCommit({
				asset,
				deviceActionType,
				targetValue,
				dataPoint: undefined,
				targetFrequency,
				availableElements,
				selectedElement: undefined
			});
		}
	};
	onElementSelect = (elementName: string) => {
		const {asset, availableElements, deviceActionType} = this.state;
		const selectedElement = availableElements.find((element) => element.name === elementName);

		if (selectedElement) {
			const dataPoint = selectedElement.dataPoint;
			let targetValue = undefined;

			switch (selectedElement.name) {
				case DATAPOINT_BUTTON_COLOR1:
				case DATAPOINT_BUTTON_COLOR2:
				case DATAPOINT_LED_COLOR:
					targetValue = '#000000';
					break;

				// BLACK is LED OFF
				case DATAPOINT_PIN1:
				case DATAPOINT_PIN2:
				case DATAPOINT_PIN3:
				case DATAPOINT_PIN4:
					targetValue = GPIO_ACTION_VALUE.HIGH;
					break;

				default:
					targetValue = undefined;
			}

			if (asset) {
				this.validateAndCommit({
					asset,
					deviceActionType,
					targetValue,
					dataPoint,
					targetFrequency: undefined,
					availableElements,
					selectedElement
				});
			}
		}
	};
	onTargetValueSelect = (targetValue: string | null | undefined) => {
		const {asset, deviceActionType, dataPoint, availableElements, targetFrequency} = this.state;
		this.validateAndCommit({
			asset,
			deviceActionType,
			targetValue,
			dataPoint,
			targetFrequency,
			availableElements
		});
	};
	onFrequencyChange = (targetFrequency: number | null | undefined) => {
		const {asset, deviceActionType, dataPoint, availableElements, targetValue} = this.state;
		this.validateAndCommit({
			asset,
			deviceActionType,
			targetValue,
			dataPoint,
			targetFrequency,
			availableElements
		});
	};
	buildAction = (uuid: string): UiDeviceAction => {
		const {asset, deviceActionType, targetValue, dataPoint, targetFrequency} = this.state;
		const isValid = validateDeviceActionState(this.state);
		const dataAddress = {
			assetUrn: asset ? asset.urn : undefined,
			assetId: asset ? asset.id : undefined,
			dataPoint
		};
		return {
			actionType: ACTION_TYPE_DEVICE,
			deviceActionType: deviceActionType,
			dataAddress,
			targetValue,
			targetFrequency,
			metadata: {
				uuid,
				isValid
			}
		};
	};
	validateAndCommit = (modifiedState: Partial<DeviceActionState>) => {
		const {
			onChange,
			index,
			action: {
				metadata: {uuid}
			}
		} = this.props;
		this.setState({...modifiedState} as DeviceActionState, () => {
			onChange(this.buildAction(uuid), index);
		});
	};

	render() {
		const {assetGroup, canEdit} = this.props;
		const {
			asset,
			deviceActionType,
			availableElements,
			targetValue,
			selectedElement,
			targetFrequency,
			availablePins
		} = this.state;
		return (
			<div className="rule-action-item" data-testid="device-action">
				<DeviceAndActionIcon asset={asset} type={ACTION_TYPE_DEVICE} />
				<RuleAsset assetGroup={assetGroup} onAssetSelect={this.onAssetSelect} asset={asset} canEdit={canEdit} />
				{asset && (
					<div className="action-element">
						<RuleDeviceActionType
							deviceActionType={deviceActionType}
							onActionTypeSelect={this.onActionTypeSelect}
							canEdit={canEdit}
							availablePins={availablePins}
							asset={asset}
						/>
					</div>
				)}
				{asset && deviceActionType && (
					<div className="action-datapoint">
						<RuleActionDatapoint
							selectedElement={selectedElement}
							onElementSelect={this.onElementSelect}
							availableElements={availableElements}
							canEdit={canEdit}
						/>
					</div>
				)}
				{selectedElement && (
					<RuleActionDeviceValue
						element={selectedElement}
						value={targetValue}
						frequency={targetFrequency}
						onValueSelect={this.onTargetValueSelect}
						onFrequencyChange={this.onFrequencyChange}
						canEdit={canEdit}
					/>
				)}
			</div>
		);
	}
}
